import type { CVListItemDto } from '@cibli/shared';

import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@/contexts/auth.context';
import { onDataChanged } from '@/lib/dataEvents';
import { getUserCVs } from '@/services/cv';

interface UseCVListReturns {
  cvs: CVListItemDto[];
  filteredCVs: CVListItemDto[];
  isLoading: boolean;
  error: Error | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  refetch: () => Promise<void>;
}

export function useCVList(): UseCVListReturns {
  const { user, isLoading: authLoading } = useAuth();
  const [cvs, setCVs] = useState<CVListItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCVs = useCallback(
    async (search?: string) => {
      if (authLoading) {
        setIsLoading(true);
        return;
      }

      if (!user?.phoneNumber) {
        setIsLoading(false); // authenticated but no phone number
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const trimmedSearch = search?.trim() || undefined;
        const data = await getUserCVs(trimmedSearch);
        setCVs(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch CVs'));
      } finally {
        setIsLoading(false);
      }
    },
    [authLoading, user?.phoneNumber],
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      void fetchCVs(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [fetchCVs, searchTerm]);

  // Keep counter in sync when a CV is created/deleted from anywhere in the app
  useEffect(() => {
    return onDataChanged('cvs-changed', () => {
      void fetchCVs(searchTerm);
    });
  }, [fetchCVs, searchTerm]);

  const filteredCVs = cvs;

  const refetch = useCallback(async () => {
    await fetchCVs(searchTerm);
  }, [fetchCVs, searchTerm]);

  return {
    cvs,
    filteredCVs,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    refetch,
  };
}
